// src/components/VerificationScreen.js
import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ArrowRight, Mail, Loader2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import UserService from "../lib/UserService";
import API_CONFIG from "../lib/ApiConfig";
import track from "../lib/analytics/events";

const Verification = ({ onComplete, onFullApp }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    loginWithGoogle,
    sendSmsCode,
    verifySmsCode,
    loading: authLoading,
    error: authError,
    resetRecaptcha,
    loginWithCustomToken,
    sendPasswordlessLink,
    completePasswordlessLogin,
    isPasswordlessLink,
    user,
    firebaseUser,
    getIdToken,
  } = useAuth();

  const [verificationMethod, setVerificationMethod] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  // const [password, setPassword] = useState(""); // Removing password state
  const [showEmailInput, setShowEmailInput] = useState(true);
  // const [showPasswordInput, setShowPasswordInput] = useState(false); // Removing password UI state
  const [emailLinkSent, setEmailLinkSent] = useState(false); // New state for "Check Email" screen
  // const [isNewUser, setIsNewUser] = useState(false); // No longer needed
  const recaptchaRef = useRef(null);
  const shouldHideForm = user && firebaseUser;

  // Update error message when auth error changes
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  // Check if user is already authenticated
  useEffect(() => {
    // Check if this is a magic link re-entry
    // Only attempt to complete login if we aren't already logged in
    if (!user && isPasswordlessLink(window.location.href)) {
      setLoading(true);
      completePasswordlessLogin()
        .then((user) => {
          console.log("Magic link login successful:", user.uid);
          track.emailLinkCompleted({ uid: user.uid });
          track.identify(user.uid, {
            email: user.email,
            source: "nationwide",
            auth_method: "email_link",
          });
        })
        .catch((err) => {
          console.error("Magic link failed:", err);
          if (err.message === "MISSING_EMAIL") {
            // In a real app, you would show a prompt here.
            // For now, let's just show the error.
            setError("Please enter your email again to confirm the sign in.");
            setShowEmailInput(true);
            // The standard UI will be shown, allowing them to enter email.
            // However, standard UI 'send' button sends a new link.
            // To truly support cross-device, we need a specific 'Confirm Email' UI.
            // Simplified for this iteration: Just tell them to use the same device.
            setError(
              "It looks like you opened the link on a different device. Please try logging in again on this device."
            );
          } else {
            setError(err.message);
          }
        })
        .finally(() => setLoading(false));
      return; // Don't run the rest of the auth check while processing link
    }

    const handleAuthentication = async () => {
      if (!user || !firebaseUser) {
        return;
      }

      try {
        setLoading(true);

        // Basic verification info, independent of server customer match
        const verificationData = {
          method: user.phoneNumber ? "phone" : "google",
          userId: user.id || user.uid,
          verifiedAt: new Date().toISOString(),
        };

        sessionStorage.setItem(
          "verificationData",
          JSON.stringify(verificationData)
        );

        track.identify(user.id || user.uid, {
          email: user.email,
          name: user.displayName,
          source: "nationwide",
          auth_method: verificationData.method,
        });

        // Extended warranty keeps callback-based behavior
        // Insurance profile flow: load customer data but always go to rewards
        const email = user.email;
        const phone = user.phoneNumber;

        // Call getOrCreateUser to perform server-side token exchange/creation
        try {
          const token = await getIdToken(true);

          // Get referral code if exists (or default)
          const referralCode = sessionStorage.getItem("referralCode") || "nationwide";

          const serverUser = await UserService.getOrCreateUser(
            token,
            {
              email: email,
              phoneNumber: phone,
              displayName: user.displayName,
              photoUrl: user.photoURL,
              emailVerified: user.emailVerified,
            },
            referralCode
          );

          console.log("Server user synced:", serverUser);

          if (serverUser) {
            sessionStorage.setItem("userData", JSON.stringify(serverUser));
          }
        } catch (serverError) {
          console.error("Failed to sync user with server:", serverError);
          // We generally proceed even if this fails, but you might want to block or retry
          // For now, logging it and letting the loadCustomer flow continue (or fail if dependent)
        }

        // if (email || phone) {
        //   try {
        //     const token = await getIdToken(true);
        //     // We use the same token we just got
        //     const {
        //       customer,
        //       notFound,
        //     } = await UserService.loadCustomerByEmailOrPhone({
        //       email,
        //       phone,
        //       firebaseToken: token,
        //     });

        //     if (!notFound && customer) {
        //       // Persist customer for rewards & other screens
        //       try {
        //         sessionStorage.setItem(
        //           "customerData",
        //           JSON.stringify(customer)
        //         );
        //       } catch (storageError) {
        //         console.error(
        //           "Failed to persist customerData after verification:",
        //           storageError
        //         );
        //       }

        //       try {
        //         const existingUserData = JSON.parse(
        //           sessionStorage.getItem("userData") || "{}"
        //         );
        //         // Merge, prioritizing customer ID if needed, but usually they are distinct concepts
        //         const mergedUserData = { ...existingUserData, customer };
        //         sessionStorage.setItem(
        //           "userData",
        //           JSON.stringify(mergedUserData)
        //         );
        //       } catch (storageError) {
        //         console.error(
        //           "Failed to persist userData after verification:",
        //           storageError
        //         );
        //       }
        //     }
        //   } catch (customerError) {
        //     console.error(
        //       "Error loading customer after verification:",
        //       customerError
        //     );
        //   }
        // }

        navigate("/alarm/verify", { replace: true });
      } catch (err) {
        console.error("Authentication handling error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    // Run the authentication handler
    handleAuthentication();
  }, [user, firebaseUser, navigate, getIdToken, onComplete]);

  const handleGoogleAuth = async () => {
    try {
      setError(null);
      sessionStorage.setItem("userLoginType", "Email");
      track.googleSigninInitiated();
      const user = await loginWithGoogle();

      if (user) {
        console.log("Google Auth successful:", user.uid);
        track.googleSigninSucceeded({ uid: user.uid });
      }
    } catch (err) {
      track.googleSigninFailed({ error: err?.message });
      setError("Google authentication failed. Please try again.");
    }
  };

  const handlePhoneSubmit = async () => {
    try {
      if (!phoneNumber.match(/^\d{10}$/)) {
        throw new Error("Please enter a valid 10-digit phone number");
      }
      sessionStorage.setItem("userLoginType", "Phone");
      // Format phone number for international format (assuming US)
      const formattedPhone = `+1${phoneNumber}`;
      console.error(formattedPhone);
      // Make sure recaptchaRef.current exists
      if (!recaptchaRef.current) {
        throw new Error("reCAPTCHA container not found");
      }
      console.error(formattedPhone);

      // Pass the DOM element instead of the string
      const success = await sendSmsCode(formattedPhone, recaptchaRef.current);
      // Send SMS verification code
      if (success) {
        console.log("SMS sent to:", formattedPhone);
        setOtpSent(true);
        setError(null);
      }
    } catch (err) {
      setError(err.message);
      resetRecaptcha();
    }
  };

  const handleEmailButtonClick = () => {
    setShowEmailInput(true);
    // setShowPasswordInput(false);
    setError(null);
  };

  // Instant login (default) skips Firebase email verification: the backend vends a
  // custom token for any email present in the Nationwide import list, and we sign in
  // silently. Set REACT_APP_USE_INSTANT_LOGIN=false (requires rebuild) to fall back to
  // the Firebase passwordless magic-link flow below.
  const useInstantLogin = process.env.REACT_APP_USE_INSTANT_LOGIN !== "false";

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email");
      return;
    }

    const emailDomain = email.split("@")[1] || null;
    track.emailLinkRequested({ email_domain: emailDomain });

    setLoading(true);
    setError(null);

    if (useInstantLogin) {
      try {
        const url =
          API_CONFIG.getUrl(API_CONFIG.ENDPOINTS.NATIONWIDE.VERIFY_EMAIL) +
          "?email=" +
          encodeURIComponent(email.trim());
        const response = await fetch(url, { method: "POST" });

        if (response.status === 404) {
          track.emailLinkRequestFailed({ reason: "not_nationwide_email" });
          setError(
            "This email isn't associated with a Nationwide reward. Please use the activation link from your Nationwide email."
          );
          return;
        }

        const data = await response.json();
        if (!data.success) {
          track.emailLinkRequestFailed({ reason: "server_error" });
          setError(data.error || "Sign in failed. Please try again.");
          return;
        }

        sessionStorage.setItem("userLoginType", "Email");
        await loginWithCustomToken(data.sessionToken);
      } catch (error) {
        track.emailLinkRequestFailed({
          reason: "server_error",
          message: error?.message,
        });
        setError(error.message || "Sign in failed. Please try again.");
      } finally {
        setLoading(false);
      }
    } else {
      // Firebase passwordless magic-link fallback (legacy behavior).
      try {
        const matched = await UserService.isNationwideMatchedUser(email);
        if (!matched) {
          track.emailLinkRequestFailed({ reason: "not_nationwide_email" });
          setError(
            "This email isn't associated with a Nationwide reward. Please use the activation link from your Nationwide email."
          );
          setLoading(false);
          return;
        }
        await sendPasswordlessLink(email);
        setEmailLinkSent(true);
      } catch (error) {
        track.emailLinkRequestFailed({
          reason: "server_error",
          message: error?.message,
        });
        setError(error.message || "Failed to send login link.");
      } finally {
        setLoading(false);
      }
    }
  };

  // Phone verification analytics intentionally not wired — phone-based verification
  // is on its way out of the Nationwide flow. If/when it's reinstated, add
  // track.phoneOtpRequested / phoneOtpVerified / phoneOtpFailed here.

  // Removed handlePasswordSubmit

  const handleOtpSubmit = async (code) => {
    try {
      if (!code.match(/^\d{6}$/)) {
        throw new Error("Please enter a valid 6-digit OTP");
      }

      const user = await verifySmsCode(code);

      if (user) {
        console.log("Phone verification successful:", user.uid);
        // Close the verification modal immediately after successful verification
        setOtpSent(false);
        // The useEffect will handle API call and navigation
      } else {
        throw new Error("Verification failed. Please try again.");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const renderPhoneVerification = () => {
    if (!otpSent) {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-base text-darkGrey mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) =>
                setPhoneNumber(e.target.value.replace(/\D/g, ""))
              }
              placeholder="1234567890"
              autoFocus
              className="w-full"
              maxLength={10}
            />
          </div>
          <div id="recaptcha-container" ref={recaptchaRef}></div>
          <button
            onClick={handlePhoneSubmit}
            disabled={authLoading || loading || !phoneNumber}
            className="w-full flex items-center justify-center gap-2 uppercase font-bold bg-crimsonGlory text-white px-4 py-3 rounded-lg hover:bg-veryDarkGrey transition-colors disabled:bg-none disabled:bg-gray-300"
          >
            {authLoading || loading ? "Sending..." : "Send verification code"}
            {/* <ArrowRight className="w-4 h-4" /> */}
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Verification Code
          </label>
          <input
            type="text"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            placeholder="Enter 6-digit code"
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            maxLength={6}
          />
        </div>
        <button
          onClick={handleOtpSubmit}
          disabled={authLoading || loading}
          className="w-full flex items-center justify-center gap-2 bg-crimsonGlory text-white px-4 py-3 rounded-lg hover:bg-veryDarkGrey transition-colors disabled:opacity-50"
        >
          {authLoading || loading ? "Verifying..." : "Verify Code"}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const renderCheckEmailScreen = () => (
    <div className="space-y-6 text-center">
      <div className="flex justify-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
          <Mail className="w-8 h-8 text-blue-600" />
        </div>
      </div>
      <div>
        <h3 className="text-xl font-bold mb-2">Check your inbox</h3>
        <p className="text-lg text-gray-600">
          We sent a secure login link to <br />
          <span className="font-semibold">{email}</span> from our Reward Center partner Ownli.
        </p>
      </div>
      <p className="text-sm text-gray-500">
        Click the link in the email to sign in. <br />
        You can close this tab.
        <div className="text-lg bg-crimsonGlory/10 text-crimsonGlory border-2 border-crimsonGlory/30 rounded-sm px-4 py-2 mt-4 flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 mr-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          This email was sent from noreply@ownli.app. If you don’t see it in
          your inbox, please check your spam or junk folder.
        </div>
      </p>

      <button
        onClick={() => setEmailLinkSent(false)}
        className="text-crimsonGlory hover:underline font-medium"
      >
        Use a different email
      </button>
    </div>
  );

  const renderEmailVerification = () => {
    if (emailLinkSent) {
      return renderCheckEmailScreen();
    }

    return (
      <div className="space-y-4">
        {error && (
          <div className="text-red-600 text-sm p-2 bg-red-50 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleEmailSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              required
              autoFocus
              type="email"
              value={email}
              placeholder="Enter your email"
              className="w-full p-3 border rounded-lg"
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <button
            type="submit"
            disabled={!email || loading}
            className="w-full flex items-center justify-center gap-2 bg-crimsonGlory text-white px-4 py-3 rounded-lg hover:bg-veryDarkGrey transition-colors disabled:opacity-50 disabled:bg-veryDarkGrey disabled:hover:bg-veryDarkGrey"
          >
            {authLoading || loading ? (
              <span className="flex items-center justify-center">
                <Loader2 className="animate-spin h-5 w-5 mr-2" />
                {useInstantLogin ? "Signing in..." : "Sending Link..."}
              </span>
            ) : useInstantLogin ? (
              "Continue"
            ) : (
              "Send Login Link"
            )}
          </button>
        </form>
      </div>
    );
  };

  return (
    <div className="space-y-6 p-4 stagger-children">
      {/* Header Section */}
      <div className="text-center">
        <h1 className="text-xl uppercase font-bold mb-2">
          Activate Your Nationwide Account
        </h1>
        <p className="text-lg text-darkGrey">
          Once you sign in, you will see what you have earned, why you earned
          it, and can claim your rewards anytime.
        </p>
        <div className="text-lg bg-crimsonGlory text-white border-2 border-crimsonGlory rounded-sm px-4 py-2 mt-4">
          Use the same email that you use to log into your Nationwide portal.
        </div>
      </div>

      {/* Loading State */}
      {(authLoading || loading) && !verificationMethod && (
        <Card>
          <CardContent className="py-6 flex justify-center">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-crimsonGlory border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-600">Verifying your identity...</span>
            </div>
          </CardContent>
        </Card>
      )}

      <div style={{ display: shouldHideForm ? "none" : "block" }}>
        {/* Hiding Separator if link sent to be cleaner */}

        <div className="space-y-4">
          {renderEmailVerification()}

          {/* Phone Verification Modal using Radix UI */}
          {/*otpSent && (
            <PhoneVerificationModal
              authLoading={authLoading}
              loading={loading}
              open={otpSent}
              onOpenChange={(open) => {
                if (!open) setOtpSent(false);
              }}
              onVerify={async (code) => {
                await handleOtpSubmit(code);
              }}
              onResend={async () => {
                await handlePhoneSubmit();
              }}
              error={
                error &&
                (error.toLowerCase().includes("otp") ||
                  error.toLowerCase().includes("code") ||
                  error.toLowerCase().includes("verification"))
                  ? error
                  : ""
              }
            />
          )*/}

          {/* Error Display - Only for Phone flow or Global errors not handled by Email form */}
          {error && !showEmailInput && (
            <div className="text-red-600 text-sm p-2 bg-red-50 rounded-lg">
              {error}
            </div>
          )}

          {/* Hide Google button if showing email link sent screen, to focus user on the task */}
          {/*!emailLinkSent && (
            <button
              type="button"
              className="flex items-center w-full gap-4 p-4 border border-grey rounded-lg shadow-sm hover:bg-gray-100 transition"
              onClick={handleGoogleAuth}
            >
              <img src={googleIcon} alt="Google" />

              <div className="text-left">
                <p className="text-lg font-medium text-gray-900">
                  Sign up with Google
                </p>
                <p className="text-sm text-gray-500">
                  Verify using your Google account
                </p>
              </div>
            </button>
          )*/}
        </div>
      </div>
    </div>
  );
};

export default Verification;
